import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import { fmtEur, fmtDate } from "./format";

function capitalize(s) {
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const columns = [
  { Header: "Title", accessor: "title" },
  { Header: "Owner", accessor: "owner", Cell: ({ value }) => value || "—" },
  { Header: "Committed", accessor: "committed", align: "right", Cell: ({ value }) => fmtEur(value) },
  { Header: "Realized", accessor: "realized", align: "right", Cell: ({ value }) => fmtEur(value) },
  {
    Header: "Est. verified",
    accessor: "estimatedRealized",
    align: "right",
    Cell: ({ value }) => (value == null ? "—" : fmtEur(value)),
  },
  { Header: "Target", accessor: "targetDate", Cell: ({ value }) => fmtDate(value) },
  // No matching StatusPill vocabulary exists for "committed"/"realized" (see
  // src/views/Budgets/components/StatusPill.jsx's STATUS_CONFIG) - mirroring
  // PreventiveActionsBlock's own Status column, a plain capitalized label avoids forcing an
  // unrelated pill color/label onto this two-value status.
  { Header: "Status", accessor: "status", Cell: ({ value }) => capitalize(value) },
];

/**
 * ImplementedActionsTableBlock - Page 2's committed + realized savings actions
 * (implemented_actions_table block type).
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/implemented-actions ->
 * envelope key "implemented_actions" -> boardfigures.ImplementedActions { rows:
 * [ImplementedRow] } (internal/boardfigures/savings.go's BuildImplementedActions), sorted
 * realized desc then committed desc. Money is EUR float (fmtEur direct, no ÷100).
 */
const ImplementedActionsTableBlock = ({ title = "Committed & realized", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/implemented-actions`
  );

  const rows = data?.implemented_actions?.rows ?? [];

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-implemented-actions">
      {rows.length > 0 ? (
        <FlexTable columns={columns} data={rows} getRowId={(row) => row.id} />
      ) : (
        // No committed/realized items yet: a calm note instead of bare column headers
        // (FlexTable always draws its header group even with zero data rows).
        <p className="a4-empty-note" style={{ color: "#6b6b6b", fontSize: "9pt", margin: "2mm 0" }}>
          No committed or realized savings actions yet.
        </p>
      )}
    </BaseBlock>
  );
};

ImplementedActionsTableBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ImplementedActionsTableBlock;
